// =====================================================
// HOOK: useFiles
// Description: Hook pour gérer les fichiers des entités (contacts, équipages)
// =====================================================

import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { EntityFile, EntityType, UploadFileInput } from '../types/file';

// Le lien n'est utilisé qu'à l'ouverture immédiate du fichier.
const FILE_LINK_TTL_SECONDS = 60;

const DELETE_FILE_ERROR = 'Erreur lors de la suppression du fichier';

// Rôles autorisés à supprimer une ligne `files` (RLS files_superadmin_admin_all, 00004).
const FILE_DELETE_ROLES = ['superadmin', 'admin'];

export type DeleteFileResult = { ok: true } | { ok: false; message: string };

export function useFiles() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Récupérer les fichiers d'une entité
   */
  const getFilesByEntity = useCallback(
    async (entityType: EntityType, entityId: string): Promise<EntityFile[]> => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from('files')
          .select('*')
          .eq('entity_type', entityType)
          .eq('entity_id', entityId)
          .order('created_at', { ascending: false });

        if (fetchError) {
          console.error('Error fetching files:', fetchError);
          setError(fetchError.message);
          return [];
        }

        return data || [];
      } catch (err) {
        console.error('Error fetching files:', err);
        setError('Une erreur est survenue lors de la récupération des fichiers');
        return [];
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Upload un fichier et créer l'entrée en base de données
   */
  const uploadFile = useCallback(async (input: UploadFileInput): Promise<EntityFile | null> => {
    setLoading(true);
    setError(null);

    try {
      const { entity_type, entity_id, file, name } = input;

      // Générer un nom de fichier unique
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${entity_type}/${entity_id}/${timestamp}-${sanitizedFileName}`;

      // Upload le fichier dans le storage
      const { error: uploadError } = await supabase.storage
        .from('entity-files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        console.error('Error uploading file:', uploadError);
        setError(uploadError.message);
        return null;
      }

      // Créer l'entrée en base de données
      const { data: user } = await supabase.auth.getUser();

      const { data, error: insertError } = await supabase
        .from('files')
        .insert({
          entity_type,
          entity_id,
          name,
          file_path: filePath,
          file_size: file.size,
          mime_type: file.type || 'application/octet-stream',
          uploaded_by: user.user?.id || null,
        })
        .select()
        .single();

      if (insertError) {
        console.error('Error creating file record:', insertError);
        // Essayer de supprimer le fichier uploadé en cas d'erreur
        await supabase.storage.from('entity-files').remove([filePath]);
        setError(insertError.message);
        return null;
      }

      return data;
    } catch (err) {
      console.error('Error uploading file:', err);
      setError("Une erreur est survenue lors de l'upload du fichier");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Supprimer un fichier, de façon rejouable :
   *   1. rôle lu en base (current_user_role, la source de la RLS de `files`,
   *      00004 : admin et superadmin) : sans droit, rien n'est touché ;
   *   2. objet stocké : tant que Storage n'a pas confirmé, la ligne reste et la
   *      suppression se relance depuis la fiche ;
   *   3. ligne `files`, en vérifiant ce qui est parti.
   * Ni PostgREST ni l'API Storage ne signalent un refus RLS (« 0 élément
   * supprimé », sans erreur) : aucun succès sans ligne réellement supprimée.
   */
  const deleteFile = useCallback(async (file: EntityFile): Promise<DeleteFileResult> => {
    setLoading(true);
    setError(null);

    function fail(message: string): DeleteFileResult {
      setError(message);
      return { ok: false, message };
    }

    try {
      const { data: role, error: roleError } = await supabase.rpc('current_user_role');
      if (roleError) {
        console.error('Error reading current role:', roleError);
        return fail(DELETE_FILE_ERROR);
      }
      if (!FILE_DELETE_ROLES.includes(role as string)) {
        return fail(
          'Suppression impossible : seuls les administrateurs peuvent supprimer un fichier.'
        );
      }

      // Une liste vide sans erreur veut dire « objet déjà absent » (reprise
      // après une ligne non supprimée) : un admin n'est jamais refusé par la
      // RLS de Storage sur ce bucket.
      const { error: storageError } = await supabase.storage
        .from('entity-files')
        .remove([file.file_path]);
      if (storageError) {
        console.error('Error deleting file from storage:', storageError);
        return fail('Le fichier n’a pas pu être supprimé. Réessayez dans un instant.');
      }

      const { data: deletedRows, error: deleteError } = await supabase
        .from('files')
        .delete()
        .eq('id', file.id)
        .select('id');
      if (deleteError || !deletedRows || deletedRows.length === 0) {
        console.error('Error deleting file record:', deleteError ?? 'no row deleted');
        return fail(
          'La suppression n’est pas terminée. Réessayez pour retirer le fichier de la fiche.'
        );
      }

      return { ok: true };
    } catch (err) {
      console.error('Error deleting file:', err);
      return fail(DELETE_FILE_ERROR);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * URL signée courte d'un fichier (SEC-06 : les PDF de BPI contiennent des
   * données personnelles, plus d'URL publique). Ouverte aussitôt dans un onglet.
   */
  const getFileUrl = useCallback(async (filePath: string): Promise<string | null> => {
    const { data, error: signError } = await supabase.storage
      .from('entity-files')
      .createSignedUrl(filePath, FILE_LINK_TTL_SECONDS);

    if (signError || !data?.signedUrl) {
      console.error('Error signing file url:', signError);
      return null;
    }
    return data.signedUrl;
  }, []);

  return {
    loading,
    error,
    getFilesByEntity,
    uploadFile,
    deleteFile,
    getFileUrl,
  };
}
